// src/contexts/MemberContext.js
import React, { createContext, useState, useContext } from 'react';
import { useSnackbar } from 'notistack';

import apiService from '../services/apiService';

// Function to transform the member data (e.g., renaming _id to id)
const transformMembers = (members) => {
  if (!Array.isArray(members)) return [];
  return members.map((member) => {
    const { _id, ...rest } = member;
    return { id: _id, ...rest };
  });
};

// Create the context
const MemberContext = createContext();

// Create a custom hook for easier access to the context
export const useMember = () => {
  return useContext(MemberContext);
};

// Export the provider to wrap around the app
export const MemberProvider = ({ children }) => {
  const [members, setMembers] = useState([]);
  const { enqueueSnackbar } = useSnackbar();

  // Function to add a new member
  const addMember = async (member) => {
    try {
      const { data } = await apiService.post('/api/v1/user', member);
      setMembers((prevUsers) => [...prevUsers, { ...data, id: data._id }]);
      enqueueSnackbar('Added new member!!!', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Add member failed!!!', { variant: 'error' });
    }
  };

  // Function to remove a member by id
  const removeMember = async (id) => {
    try {
      await apiService.delete(`/api/v1/user/${id}`);
      setMembers((prevUsers) => prevUsers.filter((member) => member.id !== id));
      enqueueSnackbar('Delete member success', { variant: 'success' });
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Delete member failed !!!', { variant: 'error' });
    }
  };

  // Function to update a member
  const updateMember = async ({ id: userId, ...updatedUser }) => {
    try {
      await apiService.patch(`/api/v1/user/${userId}`, updatedUser);
      setMembers((prevUsers) =>
        prevUsers.map((member) => {
          if (member.id === userId) {
            return { ...member, ...updatedUser };
          }
          return member;
        })
      );
      enqueueSnackbar('Update member success', { variant: 'success' });
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Can't update member !!!", { variant: 'error' });
    }
  };

  // Function to fetch members (could be from an API)
  const fetchMembers = async () => {
    const { data } = await apiService.get('/api/v1/user');
    setMembers(transformMembers(data || []));
  };

  const getMembersByTeamId = (id) => {
    if (id === 'all') return members;
    return members.filter((it) => it.team === id) || [];
  };

  return (
    <MemberContext.Provider
      value={{
        members,
        addMember,
        removeMember,
        updateMember,
        fetchMembers,
        getMembersByTeamId,
      }}>
      {children}
    </MemberContext.Provider>
  );
};
